import { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface NavItem {
  to: string;
  label: string;
  icon: string;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/admin/contacts', label: 'お問い合わせ履歴', icon: 'ri-inbox-archive-line' },
  { to: '/admin/settings/email', label: 'メール設定', icon: 'ri-mail-settings-line' },
  { to: '/admin/media', label: 'メディア', icon: 'ri-image-2-line' },
];

function resolveTitle(pathname: string): string {
  if (pathname.startsWith('/admin/contacts')) return 'お問い合わせ履歴';
  if (pathname.startsWith('/admin/settings/email')) return 'メール設定';
  if (pathname.startsWith('/admin/media')) return 'メディアライブラリ';
  return '管理画面';
}

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const title = resolveTitle(location.pathname);

  const handleSignOut = async () => {
    await signOut();
    navigate('/admin/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-background-50 text-foreground-950">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-background-200 bg-background-100 transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-16 items-center gap-2 border-b border-background-200 px-5">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-dashboard-3-line" />
          </span>
          <div className="leading-tight">
            <p className="font-heading text-sm font-semibold">管理コンソール</p>
            <p className="text-[11px] text-foreground-500">Admin Console</p>
          </div>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors ${
                  isActive
                    ? 'bg-primary-500 font-medium text-background-50'
                    : 'text-foreground-700 hover:bg-background-200'
                }`
              }
            >
              <span className="flex h-5 w-5 items-center justify-center">
                <i className={item.icon} />
              </span>
              <span className="whitespace-nowrap">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-background-200 p-3">
          <div className="mb-2 flex items-center gap-2 rounded-md bg-background-200/70 px-3 py-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary-200 text-secondary-900">
              <i className="ri-user-line text-sm" />
            </span>
            <span className="truncate text-xs text-foreground-600">{user?.email}</span>
          </div>
          <button
            type="button"
            onClick={handleSignOut}
            className="flex w-full cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-200"
          >
            <span className="flex h-5 w-5 items-center justify-center">
              <i className="ri-logout-box-r-line" />
            </span>
            ログアウト
          </button>
        </div>
      </aside>

      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-foreground-950/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-background-200 bg-background-50/90 px-4 backdrop-blur md:px-6">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-700 hover:bg-background-200 lg:hidden"
            aria-label="メニューを開く"
          >
            <i className="ri-menu-2-line text-lg" />
          </button>
          <h1 className="font-heading text-base font-semibold md:text-lg">{title}</h1>
        </header>

        <main className="px-4 py-6 md:px-6 md:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}